import type { Messages } from './en';

/**
 * Fase C: viajes en tren — Taipéi (101, MRT, Museo del Palacio) y Hualien (Taroko, Qixingtan).
 * Las líneas de práctica de inglés se quedan en inglés a propósito.
 */
export const esTrips: Pick<Messages, 'trips'> = {
  trips: {
    mapNames: { station: 'Estación de tren', taipei: 'Taipéi', mrt: 'Estación del MRT (metro)', museum: 'Museo del Palacio Nacional', hualien: 'Hualien', hotel: 'Hotel junto al mar' },
    zones: {
      station: 'Estación de tren', taipei: 'Taipéi', tower101: 'Taipéi 101', taipeiStation: 'Estación Central de Taipéi', taipeiPark: 'Parque de la ciudad',
      mrt: 'Estación del MRT (metro)', museum: 'Museo del Palacio Nacional', gorge: 'Sendero de Taroko', taroko: 'Garganta de Taroko', beach: 'Playa de Qixingtan', hualien: 'Hualien', hotel: 'Hotel junto al mar',
    },
    objects: {
      stTicket: 'Taquilla', stTrain: 'Tren', twElevator: 'Mirador del Taipéi 101', tpTrain: 'Estación Central de Taipéi', tpNoodles: 'Puesto de sopa de fideos con ternera',
      tpCakes: 'Tienda de pasteles de piña', ubike: 'Alquiler de YouBike', mrtMap: 'Mapa de líneas del MRT', mrtRules: 'Normas del MRT', mrtRide: 'Andén del MRT',
      muCabbage: 'Col de jade', muMeat: 'Piedra con forma de carne', muDing: 'Ding de bronce', muRuware: 'Cuenco de cerámica Ru', muScroll: 'Rollo de Qingming',
      muCurio: 'Caja de tesoros', muExit: 'Salida al MRT', hlHelmet: 'Préstamo de cascos', hlView: 'Mirador de la Gruta de las Golondrinas', hlStones: 'Torres de piedras',
      hlSea: 'El océano Pacífico', hlMochi: 'Tienda de mochi', hlTrain: 'Estación de Hualien',
      hlQingshui: 'Mirador de los acantilados de Qingshui', hlNineTurns: 'Túnel de las Nueve Curvas', hlPancake: 'Tortitas bomba de cebolleta', hlWonton: 'Puesto de sopa de wonton', hlAmis: 'Danza amis de la cosecha', hlWhale: 'Barco de avistamiento de ballenas', htDesk: 'Recepción del hotel', htBed: 'Tu cama del hotel',
    },
    npc: { stationClerk: 'Taquillera', guide: 'Guía de la ciudad', busker: 'Músico callejero', docent: 'Guía del museo', ranger: 'Guarda del parque', hiker: 'Excursionista', mochiAuntie: 'La tía del mochi', captain: 'Capitán del barco', receptionist: 'Recepcionista' },

    station: {
      counter: 'Good morning! Where would you like to go today?',
      dest: { taipei: 'Taipéi', hualien: 'Hualien' },
      destHint: { taipei: 'El Taipéi 101, el MRT y el Museo del Palacio Nacional', hualien: 'La Garganta de Taroko y la playa de Qixingtan' },
      price: (n: number) => `${n} monedas (ida y vuelta)`,
      howAsk: '¿Cómo lo pides? (práctica de inglés)',
      ask: ['One return ticket to {place}, please.', 'Ticket. {place}. Now.', 'I want to go, give me train.'],
      wrong: 'La taquillera sonríe: «¿Podrías pedirlo con un poco más de educación?»',
      bought: (place: string) => `🎫 Has comprado un billete de ida y vuelta a ${place}. ¡Ve al andén!`,
      poor: 'No tienes monedas suficientes para ese billete.',
      tooLate: 'El último tren del día ya ha salido. ¡Vuelve mañana por la mañana!',
      haveTicket: (place: string) => `Ya tienes un billete a ${place}. El andén está justo pasados los tornos.`,
      noTicket: 'Primero necesitas un billete: cómpralo en la taquilla.',
      board: '🚆 Subir al tren',
      boardBack: '🚆 Coger el tren de vuelta a casa',
      notNow: 'Ahora no',
      trainText: (place: string) => `El tren a ${place} espera en el andén. ¡Las puertas están abiertas!`,
      backText: 'Tu tren de vuelta a casa te espera. ¿Listo para volver?',
    },
    ride: {
      title: '🚆 Ventanilla del tren',
      toTaipei: ['Los arrozales brillan con el sol de la mañana…', 'Pasan volando pueblecitos, templos y motos…', 'Edificios altos… ¡y ahí está el Taipéi 101!'],
      toHualien: ['A la derecha se alzan montañas verdes…', 'Entramos en un túnel… ¡y salimos junto al mar!', 'Acantilados grises caen en picado sobre el Pacífico azul…'],
      home: ['El tren se mece suavemente…', 'Das una cabezadita mirando por la ventana…', '¡Ya casi estás en casa!'],
      announce: (place: string) => `“The next station is ${place}.”`,
      skip: '⏩ Saltar',
      arrive: (place: string) => `¡Bienvenido a ${place}!`,
    },
    taipei: {
      elevator: 'El ascensor al mirador de la planta 89 es uno de los más rápidos del mundo: ¡37 segundos!',
      goUp: '🛗 Subir hasta arriba (20 monedas)',
      poor: 'Necesitas 20 monedas para el mirador.',
      viewTitle: '🏙️ Taipéi 101 · planta 89',
      viewText: [
        'Toda la ciudad está a tus pies: ríos, puentes, montañas y coches diminutos.',
        'En el centro de la torre cuelga una bola dorada gigante: el amortiguador de masa. Mantiene la torre estable durante los tifones y los terremotos.',
      ],
      viewAsk: 'Responde en inglés: what keeps the tower steady?',
      viewOptions: ['A giant golden ball', 'A long rope', 'Big windows'],
      viewRight: '✅ ¡Correcto! El amortiguador pesa nada menos que 660 toneladas.',
      viewWrong: '❌ No exactamente… ¡fíjate en la bola dorada!',
      noodles: { vendor: 'Welcome! Beef noodle soup — spicy or clear broth?', ok: 'Clear broth, please. One small bowl.', bad: ['Noodles! Hurry!', 'Spicy, but not spicy, but spicy.'], done: 'Ternera tierna, fideos elásticos y un caldo sabrosísimo. ¡Te sientes superfuerte!', price: 45 },
      cakes: { vendor: 'Our pineapple cakes are fresh today! Would you like a gift box?', ok: 'Yes, please. A small box for my family, thank you!', bad: ['Cake. Box. Give.', 'I want all of them for free.'], done: 'Una caja preciosa de pasteles de piña. ¡A tu familia le van a encantar!', price: 40 },
      ubike: 'YouBike compartidas: pasa la tarjeta, coge una bici y devuélvela en cualquier estación.',
      rent: '🚲 Alquilar una bici (5 monedas)',
      ride: '¡Vas en YouBike! Recuerda: para en los semáforos en rojo.',
      returnBike: '🚲 Devolver la bici',
      returned: 'Devuelves la bici. ¡Pip!',
      onlyHere: 'Devuelves la YouBike antes de irte.',
      guide: [
        '¡Bienvenido a Taipéi! El MRT (metro) es la forma más fácil de moverse.',
        'Prueba la sopa de fideos con ternera: cada familia tiene su receta secreta.',
        'Desde el mirador del 101 se ven las montañas que rodean la ciudad.',
        '¿Perdona, me dices cómo llegar al MRT? ¡Fácil: cruza la calle y gira a la izquierda!',
      ],
      busker: 'Un músico callejero toca una melodía dulce con el erhu, un violín de dos cuerdas.',
      listen: '🎶 Escuchar y aplaudir',
      listened: 'Aplaudes al ritmo. ¡El músico hace una reverencia y sonríe!',
    },
    mrt: {
      map: 'Líneas de colores cruzan toda la ciudad: roja, azul, verde, naranja, marrón…',
      mapQuiz: 'Practica inglés: “Which line goes to the zoo?”',
      mapOptions: ['The brown line', 'The blue line', 'The bus'],
      mapRight: '✅ ¡Sí! La línea marrón va al zoo de Taipéi.',
      mapWrong: 'Mmm, ¡vuelve a mirar el mapa!',
      rules: 'Normas del MRT: ¡léelas en inglés!',
      rulesQs: [
        { q: 'Can you eat or drink on the MRT?', a: ['No, eating and drinking are not allowed.', 'Yes, a picnic is fine.', 'Only ice cream.'] },
        { q: 'Who are the dark blue priority seats for?', a: ['Elderly people, pregnant women, people with children or injuries.', 'Anyone who runs fast.', 'Only the driver.'] },
        { q: 'Where do you stand on the escalator?', a: ['On the right, so people can walk on the left.', 'In the middle, with my arms out.', 'I sit down on it.'] },
      ],
      rulesRight: '✅ ¡Correcto!',
      rulesWrong: '❌ ¡Inténtalo otra vez!',
      rulesDone: '¡Te sabes las normas del MRT como alguien de aquí!',
      platform: 'Las puertas de cristal del andén se abrirán cuando llegue el tren. ¿Adónde vas?',
      toMuseum: '🏛️ Shilin — Museo del Palacio Nacional',
      toTower: '🏙️ Taipéi 101 / World Trade Center',
      toXimen: '🛍️ Ximen — Ximending',
      toLongshan: '🏮 Templo de Longshan — Wanhua',
      toCks: '🏯 Memorial de Chiang Kai-shek — Plaza de la Libertad',
      toBeitou: '♨️ Xinbeitou — aguas termales de Beitou',
      riding: '¡Din-don! “Please mind the gap.”',
    },
    museum: {
      intro: 'Practica inglés: lee cada cartel. La guía te hará una pregunta sobre él.',
      items: {
        muCabbage: {
          label: 'Jadeite Cabbage. Carved from one piece of jade, half green and half white. Look closely: two little insects hide on the leaves!',
          q: 'What is hiding on the cabbage?',
          a: ['Two little insects', 'A cat', 'A golden coin'],
        },
        muMeat: {
          label: 'Meat-shaped Stone. It looks just like a piece of braised pork, but it is a stone called jasper. The skin even has tiny pores!',
          q: 'What is the “meat” really made of?',
          a: ['Stone', 'Real pork', 'Chocolate'],
        },
        muDing: {
          label: 'Bronze ding. A cooking pot with three legs, almost three thousand years old. It has a long text written inside.',
          q: 'How many legs does a ding have?',
          a: ['Three', 'Four', 'None'],
        },
        muRuware: {
          label: 'Ru ware basin. Sky-blue pottery with fine cracks in the glaze. Fewer than one hundred pieces exist in the whole world.',
          q: 'What colour is Ru ware?',
          a: ['Sky blue', 'Bright red', 'Black'],
        },
        muScroll: {
          label: 'Along the River During the Qingming Festival. A painting more than eleven metres long, full of boats, bridges, shops and thousands of tiny people.',
          q: 'What can you see in the painting?',
          a: ['Boats, bridges and tiny people', 'Only one big tree', 'A football match'],
        },
        muCurio: {
          label: 'Curio box. A small wooden box with secret drawers, made for an emperor to keep his tiny treasures.',
          q: 'Who was the box made for?',
          a: ['An emperor', 'A fisherman', 'A cat'],
        },
      },
      right: '✅ ¡Muy bien leído!',
      wrong: '❌ ¡Vuelve a leer el cartel!',
      seen: (n: number, total: number) => `Tesoros estudiados: ${n}/${total}`,
      exit: '¿Coges el MRT para volver a la ciudad?',
      ride: '🚇 Coger el MRT',
      docent: [
        'Bienvenido al Museo del Palacio Nacional. Estos tesoros tienen cientos, ¡incluso miles!, de años.',
        'Por favor, no uses flash al hacer fotos. La luz puede estropear las pinturas antiguas.',
        '¿Nuestra pieza más famosa? La Col de jade. ¡Todo el mundo quiere ver los insectitos!',
      ],
    },
    shuttle: {
      stop: 'Bus turístico de Taiwán — ruta de Taroko',
      ask: 'Excuse me, does this bus go to Taroko Gorge?',
      where: '¿Adónde quieres ir?',
      city: "🏙️ Estación de Hualien · Dongdamen · Ji'an",
      taroko: '⛰️ Garganta de Taroko',
      qixingtan: '🌊 Playa de Qixingtan',
      riding: 'El autobús serpentea por la carretera de la costa…',
      here: '¡Ya estás aquí!',
    },
    hualien: {
      helmet: 'En la garganta pueden caer piedras. ¡En los senderos todo el mundo lleva casco!',
      rent: '⛑️ Pedir un casco prestado (gratis)',
      wearing: 'Te pones un casco amarillo chillón. ¡La seguridad es lo primero!',
      already: 'Ya llevas un casco puesto.',
      needHelmet: '⛑️ El guarda te para: «¡Ponte el casco en el sendero, por favor!»',
      view: 'Gruta de las Golondrinas: los acantilados están llenos de agujeritos donde las golondrinas hacen sus nidos. El río ruge muy abajo.',
      look: '🔭 Mirar con atención',
      looked: 'Las golondrinas entran y salen de los agujeros. ¡El agua es turquesa por el polvo de mármol!',
      viewHelmet: 'Aquí deberías llevar casco. Pide uno prestado en el centro de visitantes.',
      stones: 'Hay cantos rodados lisos por todas partes. La gente los apila en torrecitas.',
      stack: '🪨 Apilar piedras',
      sea: 'El océano Pacífico. La siguiente tierra en esa dirección está… ¡muy, muy lejos!',
      listen: '🌊 Escuchar las olas',
      listened: 'Fsss… crrrsh… los guijarros ruedan con cada ola. Qué relajante.',
      welcome: '花蓮歡迎您 — ¡Bienvenido a Hualien! Montañas a un lado y el Pacífico al otro.',
      qingshui: 'Acantilados de Qingshui: las montañas verdes caen en picado sobre el océano Pacífico azul, ¡casi mil metros! Hay una carretera excavada en el acantilado.',
      lookCliff: '🏔️ Mira a lo largo de la costa',
      lookedCliff: 'El mar es azul oscuro junto a los acantilados y turquesa cerca de la orilla. Abajo corre una larga línea blanca de olas.',
      nineTurns: 'El Túnel de las Nueve Curvas: el sendero serpentea por el mármol como una culebra. ¡Mira arriba, los acantilados casi se tocan!',
      // English practice: food orders below.
      pancake: { vendor: 'Bomb scallion pancake! With an egg inside — spicy sauce or not?', ok: 'A little spicy sauce, please. One with an egg!', bad: ['Pancake. Egg. Quick.', 'Make it boom!'], done: 'Crujiente, hojaldrada y con el huevo jugoso por dentro. ¡Quema, quema, quema… pero está buenísima!', price: 35 },
      wonton: { vendor: 'Hualien wonton soup — thin skins, pork inside. Small or large bowl?', ok: 'A small bowl, please. Thank you!', bad: ['Soup! Big! Now!', 'Wonton, no soup, no wonton.'], done: 'Wontons suaves en una sopa ligera y clara con apio. ¡Un clásico de Hualien!', price: 40 },
      amis: 'Los amis, el pueblo indígena más grande de Taiwán, bailan en la fiesta de la cosecha de verano (Ilisin). Se cogen de las manos en un gran círculo y cantan juntos.',
      watchDance: '💃 Mira y da palmas',
      danced: '¡Una señora te sonríe y te mete en el círculo! Paso, paso, patada… Aprendes los primeros pasos del baile.',
      whale: 'Desde el puerto de Hualien salen excursiones para ver ballenas. Los delfines viven aquí todo el año y a veces pasan ballenas.',
      boat: (n: number) => `⛴️ Sube al barco (${n} monedas, 1 hora)`,
      sightings: [
        '¡Delfines acróbatas! Decenas de ellos saltan y giran junto al barco. ¡Todo el mundo aplaude!',
        'Un enorme cachalote echa el aire —¡pfffff!— y enseña la cola antes de sumergirse.',
        'Los peces voladores rozan las olas y una familia de delfines mulares nada al lado del barco.',
      ],
      seaFact: 'Práctica de inglés: “Dolphins are mammals. They breathe air, just like us.”',
      hotel: {
        desk: 'Welcome to the Seaside Hotel! Would you like a room for tonight?',
        price: (n: number) => `🛏️ Reserva una habitación (${n} monedas)`,
        howAsk: '¿Cómo lo pides? (práctica de inglés)',
        ask: ['I’d like a room for one night, please.', 'Room. Now. One.', 'Give me the biggest room for free.'],
        wrong: 'La recepcionista sonríe: «¿Podrías pedirlo con un poco más de educación?»',
        booked: '🔑 Aquí tienes la llave: habitación 302, con vistas al mar. ¡Duerme aquí esta noche y mañana coge el tren de vuelta a casa!',
        already: 'Ya tienes habitación para esta noche. La 302 está pasando la puerta de la derecha.',
        poor: 'No tienes suficientes monedas para una habitación.',
        bed: 'Una cama blandita con sábanas blancas y limpias, y el sonido de las olas por la ventana.',
        sleep: '😴 A dormir',
        noRoom: 'Esta cama es del hotel. Primero reserva una habitación en recepción.',
        wake: '🌅 ¡Buenos días desde Hualien! El sol sale sobre el Pacífico. Tu billete de tren a casa sigue siendo válido.',
      },
      mochi: { vendor: 'Hualien mochi! Peanut, sesame or red bean?', ok: 'Peanut, please. Can I have two?', bad: ['Mochi now.', 'All flavours, no money.'], done: 'Blandito, elástico y dulce. ¡El tentempié favorito de Hualien!', price: 25 },
      ranger: [
        'Taroko significa «magnífico y hermoso» en la lengua del pueblo truku, que vive aquí.',
        'No te salgas del sendero y no te quites el casco, por favor.',
        'Este mármol tiene millones de años. El río fue excavando esta garganta poco a poco, día tras día.',
      ],
      hiker: [
        '¡He recorrido el sendero entero! Tengo las piernas cansadas, pero las vistas han valido la pena.',
        '¿Has visto el puente colgante? Se balancea un poco… ¡qué divertido!',
        'En Qixingtan puedes ver el amanecer sobre el océano.',
      ],
    },
  },
};
